import {
  JobDetailType,
  ItemType,
  ItemTypeEnumType,
  LearningKindType,
  LearningSummaryType,
} from "@devforce/trailhead-client-gql";
import { Logger } from "@devforce/trailhead-client-logger";
import { I18n } from "@devforce/trailhead-client-locale";
import { CommunityNodeSummaryType } from "common/contentCollection";

import { getAssetsUrl } from "common/helpers";
import type { LearningKindCollectionType, LearningType } from "common/learning";

import type { ContentSummary } from "tds/contentSummary";

import topicPlaceholder from "./assets/topic-placeholder.svg";
import userPlaceholder from "./assets/user-placeholder.png";
import appExchangeIcon from "./assets/AppExchange.svg";
import documentationIcon from "./assets/Documentation.svg";
import knownIssuesIcon from "./assets/KnownIssues.svg";
import videoIcon from "./assets/Video.svg";
import * as locales from "./locales";
import buildLink from "../helpers/buildLink";
import { formatDuration, lastActivity, timestamp } from "../helpers/time";

export type { ContentSummary };

const logger = new Logger({ prefix: "[th-compoments][contentSummary] " });
const i18n = new I18n({ contentSummary: locales });
const unifiedToLearningTypeMap = {
  [ItemTypeEnumType.BadgeType]: LearningKindType.BadgeType,
  [ItemTypeEnumType.UnitType]: LearningKindType.UnitType,
  [ItemTypeEnumType.ModuleType]: LearningKindType.ModuleType,
  [ItemTypeEnumType.ProjectType]: LearningKindType.ProjectType,
  [ItemTypeEnumType.TrailType]: LearningKindType.TrailType,
  [ItemTypeEnumType.LearningpathType]: LearningKindType.LearningpathType,
  [ItemTypeEnumType.TrailmixType]: LearningKindType.TrailmixType,
  [ItemTypeEnumType.LinkType]: LearningKindType.LinkType,
  [ItemTypeEnumType.TaskType]: LearningKindType.TaskType,
  [ItemTypeEnumType.StepType]: LearningKindType.StepType,
  [ItemTypeEnumType.SuperbadgeType]: LearningKindType.SuperbadgeType,
  // Defaults:
  [ItemTypeEnumType.CollaborationGroupType]: LearningKindType.TrailType,
  [ItemTypeEnumType.FeedItemPostType]: LearningKindType.TrailType,
  [ItemTypeEnumType.FeedItemQuestionType]: LearningKindType.TrailType,
  [ItemTypeEnumType.HelpDocsType]: LearningKindType.LinkType,
  [ItemTypeEnumType.HtDeveloperDocumentsCType]: LearningKindType.LinkType,
  [ItemTypeEnumType.HtReleaseNotesDocumentationCType]:
    LearningKindType.LinkType,
  [ItemTypeEnumType.KbGettingStartedType]: LearningKindType.LinkType,
  [ItemTypeEnumType.KbKnowledgeArticleType]: LearningKindType.LinkType,
  [ItemTypeEnumType.KbQuickStartsType]: LearningKindType.LinkType,
  [ItemTypeEnumType.KnownIssueCType]: LearningKindType.LinkType,
  [ItemTypeEnumType.ListingCType]: LearningKindType.LinkType,
  [ItemTypeEnumType.ThKnowledgeType]: LearningKindType.LinkType,
  [ItemTypeEnumType.TopicType]: LearningKindType.TrailType,
  [ItemTypeEnumType.TrailheadType]: LearningKindType.TrailType,
  [ItemTypeEnumType.UserType]: LearningKindType.TrailType,
  [ItemTypeEnumType.VideoType]: LearningKindType.LinkType,
};

function epochTimeToISOString(epochTime: ItemType["actionAt"]): string {
  if (epochTime) {
    return new Date(epochTime * 1000).toISOString();
  } else {
    return "";
  }
}

function getPluralizedLabel(rootKey: string, count: number | null = 0) {
  let labelKey;
  if (count === 1) {
    labelKey = `${rootKey}_singular`;
  } else {
    labelKey = `${rootKey}_plural`;
  }
  return i18n.t("contentSummary", labelKey, {
    count: count || 0,
  });
}

function getDocumentationPretitleLabel(type: ItemTypeEnumType) {
  switch (type) {
    case ItemTypeEnumType.HelpDocsType:
      return "help_docs_pretitle";
    case ItemTypeEnumType.HtDeveloperDocumentsCType:
      return "ht_developer_documents_c_pretitle";
    case ItemTypeEnumType.HtReleaseNotesDocumentationCType:
      return "ht_release_notes_documentation_c_pretitle";
    case ItemTypeEnumType.KbGettingStartedType:
      return "kb_getting_started_pretitle";
    case ItemTypeEnumType.KbQuickStartsType:
      return "kb_quick_starts_pretitle";
    case ItemTypeEnumType.KbKnowledgeArticleType:
      return "kb_knowledge_article_pretitle";
    case ItemTypeEnumType.KnownIssueCType:
      return "known_issue_c_pretitle";
    case ItemTypeEnumType.ThKnowledgeType:
      return "th_knowledge_pretitle";
    case ItemTypeEnumType.VideoType:
      return "video_pretitle";
    default:
      return "";
  }
}

function getCollaborationGroupPretitleLabel(status: string | null) {
  switch (status) {
    case "PRIVATE":
      return "private_collaboration_group_pretitle";
    case "PUBLIC":
      return "public_collaboration_group_pretitle";
    default:
      return "collaboration_group_pretitle";
  }
}

function getCollaborationMetaLeftHtml(unified: ItemType): string | undefined {
  const postLink = buildLink({
    href: unified.url!,
    text: i18n.t("contentSummary", "feed_item_post_go_to_post"),
    sameTab: true,
    style: "color: var(--tds-color-brand); text-decoration: none;",
  });
  switch (unified.type) {
    case ItemTypeEnumType.FeedItemQuestionType:
      return `${getPluralizedLabel(
        "feed_item_likes",
        unified.likesCount
      )} • ${getPluralizedLabel("feed_item_answers", unified.commentsCount)}`;
    case ItemTypeEnumType.FeedItemPostType:
      return `${postLink} • ${getPluralizedLabel(
        "feed_item_likes",
        unified.likesCount
      )} • ${getPluralizedLabel("feed_item_comments", unified.commentsCount)}`;
    default:
      return;
  }
}

function getDocumentationMetaLeft(unified: ItemType): string | undefined {
  switch (unified.type) {
    case ItemTypeEnumType.HtReleaseNotesDocumentationCType:
      return unified.releaseName || undefined;
    case ItemTypeEnumType.KnownIssueCType:
      return unified.status || undefined;
    case ItemTypeEnumType.ThKnowledgeType:
      return unified.keywords ? unified.keywords.join(" • ") : undefined;
    default:
      return;
  }
}

function getDocumentationMetaRight(unified: ItemType): string | undefined {
  switch (unified.type) {
    case ItemTypeEnumType.VideoType:
      return (unified.secondTotal && formatDuration(unified.secondTotal)) || "";
    default:
      return;
  }
}

function getDocumentationMetaRightHtml(unified: ItemType): string | undefined {
  switch (unified.type) {
    case ItemTypeEnumType.KnownIssueCType:
      return buildLink({
        href: unified.url!,
        text: unified.actionAt
          ? i18n.t("contentSummary", "known_issue_meta_right", {
              timestamp: timestamp(epochTimeToISOString(unified.actionAt)),
            })
          : "",
        sameTab: false,
        style: "color: var(--tds-color-meteorite);",
      });
    default:
      return;
  }
}

function getDocumentationImage(type: ItemTypeEnumType) {
  switch (type) {
    case ItemTypeEnumType.HelpDocsType:
    case ItemTypeEnumType.HtDeveloperDocumentsCType:
    case ItemTypeEnumType.HtReleaseNotesDocumentationCType:
    case ItemTypeEnumType.KbGettingStartedType:
    case ItemTypeEnumType.KbQuickStartsType:
    case ItemTypeEnumType.KbKnowledgeArticleType:
    case ItemTypeEnumType.ThKnowledgeType:
      return getAssetsUrl(documentationIcon);
    case ItemTypeEnumType.KnownIssueCType:
      return getAssetsUrl(knownIssuesIcon);
    case ItemTypeEnumType.VideoType:
      return getAssetsUrl(videoIcon);
    default:
      return "";
  }
}

export function fromLearning(learning: LearningType): ContentSummary {
  switch (learning.type as LearningKindCollectionType) {
    case "MODULE":
      return fromModule(learning);
    case "PROJECT":
      return fromProject(learning);
    case "SUPERBADGE":
      return fromSuperbadge(learning);
    case "TRAIL":
      return fromTrail(learning);
    case "LEARNINGPATH":
      return fromLearningPath(learning);
    case "TRAILMIX":
      return fromTrailmix(learning);
    default:
      throw new Error(`Unsupported type: "${learning.type}"`);
  }
}

export function fromCommunity(
  community: CommunityNodeSummaryType
): ContentSummary {
  const contentType = community.type.toLowerCase() as ContentSummary["type"];
  let avatar = community.avatar;
  if (contentType === "topic" && !avatar) {
    avatar = getAssetsUrl(topicPlaceholder);
  }
  return {
    id: community.id,
    type: contentType,
    title: community.name,
    pretitle: community.typeLocalized,
    image: avatar,
    path: community.url,
    descriptionHtml: community.description,
  };
}

function getJobSalary(minSalary: string | null, maxSalary: string | null) {
  if (minSalary && maxSalary) {
    return `${minSalary}-${maxSalary}`;
  }
  return maxSalary ?? minSalary ?? "";
}

export function fromJob(job: JobDetailType): ContentSummary {
  return {
    id: job.id,
    descriptionHtml: job.summary,
    path: job.url,
    pathTarget: "_blank",
    pretitle: job.company,
    title: job.title,
    type: "job",
    dynamicCtaIcon: true,
    dynamicCtaIconSymbol: "link",
    details: [
      {
        icon: "location",
        ariaHidden: "false",
        ariaLabel: i18n.t("contentSummary", "location"),
        label: job.location ?? "",
      },
      {
        icon: "money",
        ariaHidden: "false",
        ariaLabel: i18n.t("contentSummary", "salary"),
        label: getJobSalary(job.minSalary, job.maxSalary),
      },
      {
        icon: "clock",
        label: job.postedDate
          ? i18n.t("contentSummary", "job_detail_posted_on", {
              date: job.postedDate,
            })
          : "",
      },
    ].filter((detail) => !!detail.label),
  };
}

export function fromLearningBase(learning: LearningType): ContentSummary {
  return {
    color: learning.color ?? undefined,
    description: learning.description,
    id: learning.id,
    image: learning.iconUrl ?? "",
    estimatedTime: learning.minuteTotal ?? undefined,
    path: learning.url ?? undefined,
    points: learning.pointTotal ?? undefined,
    pretitle: i18n.t(
      "contentSummary",
      `${learning.type.toLowerCase()}_pretitle`
    ),
    title: learning.label,
    type: learning.type.toLowerCase() as ContentSummary["type"],
    user: learning.progress
      ? {
          finishedAt: learning.progress.completedDate ?? undefined,
          pointsEarned: learning.progress.earnedPoints ?? undefined,
          percentageComplete: learning.progress.completedPercent ?? undefined,
        }
      : undefined,
  };
}

export function fromModule(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromProject(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromSuperbadge(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromTrail(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromLearningPath(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromTrailmix(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
    pretitle: i18n.t("contentSummary", "trailmix_pretitle", {
      name: learning.creatorName,
    }),
  };
}

export function fromUnified(unified: ItemType): ContentSummary | null {
  switch (unified.type) {
    case ItemTypeEnumType.BadgeType:
    case ItemTypeEnumType.UnitType:
    case ItemTypeEnumType.ModuleType:
    case ItemTypeEnumType.ProjectType:
    case ItemTypeEnumType.TrailType:
    case ItemTypeEnumType.LearningpathType:
    case ItemTypeEnumType.LinkType:
    case ItemTypeEnumType.TaskType:
    case ItemTypeEnumType.StepType:
    case ItemTypeEnumType.SuperbadgeType:
      return fromLearningBase(unifiedToLearningType(unified));
    case ItemTypeEnumType.TrailmixType:
      return fromTrailmix(unifiedToLearningType(unified));
    case ItemTypeEnumType.CollaborationGroupType:
      return fromCollaborationGroup(unified);
    case ItemTypeEnumType.TopicType:
      return fromCollaborationTopic(unified);
    case ItemTypeEnumType.FeedItemQuestionType:
    case ItemTypeEnumType.FeedItemPostType:
      return fromCollaborationItem(unified);
    case ItemTypeEnumType.UserType:
      return fromCollaborationUser(unified);
    case ItemTypeEnumType.ListingCType:
      return fromListingItem(unified);
    case ItemTypeEnumType.HelpDocsType:
    case ItemTypeEnumType.HtDeveloperDocumentsCType:
    case ItemTypeEnumType.HtReleaseNotesDocumentationCType:
    case ItemTypeEnumType.KbGettingStartedType:
    case ItemTypeEnumType.KbQuickStartsType:
    case ItemTypeEnumType.KbKnowledgeArticleType:
    case ItemTypeEnumType.KnownIssueCType:
    case ItemTypeEnumType.ThKnowledgeType:
    case ItemTypeEnumType.VideoType:
      return fromDocumentationItem(unified);
    default:
      logger.warn(`Unsupported type: "${unified.type}"`);
      return null;
  }
}

function fromDocumentationItem(unified: ItemType): ContentSummary {
  return {
    id: unified.id,
    type: "documentation",
    pretitle: i18n.t(
      "contentSummary",
      getDocumentationPretitleLabel(unified.type)
    ),
    title: unified.label,
    path: unified.url || "#",
    pathTarget: "_blank",
    description: unified.description,
    metaLeft: getDocumentationMetaLeft(unified),
    metaRight: getDocumentationMetaRight(unified),
    metaRightHtml: getDocumentationMetaRightHtml(unified),
    image: getDocumentationImage(unified.type),
  };
}

function fromListingItem(unified: ItemType): ContentSummary {
  return {
    id: unified.id,
    type: "listing",
    pretitle: i18n.t("contentSummary", "listing_c_pretitle"),
    title: unified.label,
    path: unified.url || "#",
    pathTarget: "_blank",
    description: unified.description,
    metaLeftHtml: `<tds-star-rating show-label="true" rating="${unified.rating}"></tds-star-rating>`,
    metaRight:
      (!unified.free && i18n.t("contentSummary", "requires_purchase")) ||
      undefined,
    image: getAssetsUrl(appExchangeIcon),
  };
}

function fromCollaborationItem(unified: ItemType): ContentSummary {
  const actionAt = epochTimeToISOString(unified.actionAt);
  const isQuestion = unified.type === ItemTypeEnumType.FeedItemQuestionType;
  const userLink = buildLink({
    href: unified.userProfileUrl || "",
    text: unified.userName || "",
  });
  const pretitleHtml = unified.postedIn
    ? i18n.t(
        "contentSummary",
        isQuestion ? "feed_item_question_pretitle" : "feed_item_post_pretitle",
        {
          name: userLink,
          feed: buildLink({
            href: unified.postedInUrl || "",
            text: unified.postedIn || "",
          }),
        }
      )
    : userLink;
  return {
    id: unified.id,
    type: isQuestion ? "question" : "post",
    pretitleHtml,
    title: isQuestion ? unified.label : undefined,
    path: unified.url || "#",
    descriptionHtml: unified.description,
    metaLeftHtml: getCollaborationMetaLeftHtml(unified),
    metaRightHtml: buildLink({
      href: unified.url!,
      text: actionAt ? timestamp(actionAt) : "",
      sameTab: true,
      style: "color: var(--tds-color-meteorite);",
    }),
    image: unified.userImageUrl || "",
    imageAlt: unified.userName || undefined,
    imageVariant: "circle",
  };
}

function fromCollaborationGroup(unified: ItemType): ContentSummary {
  const actionAt = epochTimeToISOString(unified.actionAt);
  return {
    id: unified.id,
    type: "group",
    pretitle: i18n.t(
      "contentSummary",
      getCollaborationGroupPretitleLabel(unified.status)
    ),
    title: unified.label,
    path: unified.url || "#",
    description: unified.description,
    metaLeft: getPluralizedLabel(
      "collaboration_group_member_count",
      unified.membersCount
    ),
    metaRight: actionAt && lastActivity(actionAt),
    image: unified.iconUrl || "",
  };
}

function fromCollaborationTopic(unified: ItemType): ContentSummary {
  return {
    id: unified.id,
    type: "topic",
    pretitle: i18n.t("contentSummary", "topic_pretitle", {
      count: unified.discussingCount || 0,
    }),
    title: `#${unified.label}`,
    path: unified.url || "#",
    description: unified.description,
    image: unified.iconUrl || getAssetsUrl(topicPlaceholder),
    imageVariant: "circle",
  };
}

function fromCollaborationUser(unified: ItemType): ContentSummary {
  const actionAt = epochTimeToISOString(unified.actionAt);
  let description;
  if (unified.userRole && unified.userCompany) {
    description = i18n.t("contentSummary", "user_description", {
      role: unified.userRole,
      company: unified.userCompany,
    });
  } else {
    description = unified.userRole || unified.userCompany || undefined;
  }
  return {
    id: unified.id,
    type: "person",
    pretitle: i18n.t("contentSummary", "user_pretitle"),
    title: unified.label,
    path: unified.url || "#",
    description,
    metaLeft: unified.userLocation || "",
    metaRight: actionAt && lastActivity(actionAt),
    image: unified.userImageUrl || getAssetsUrl(userPlaceholder),
    imageVariant: "circle",
  };
}

export function unifiedToLearningType(unified: ItemType): LearningSummaryType {
  return {
    color: unified.color,
    description: unified.description,
    iconUrl: unified.iconUrl,
    id: unified.id,
    label: unified.label,
    minuteTotal: unified.minuteTotal,
    pointTotal: unified.pointTotal,
    progress: unified.progress,
    type: unifiedToLearningTypeMap[unified.type],
    url: unified.url,
    name: unified.name || "",
    creatorName: unified.creatorName,
    favorite: unified.favorite || false,
    updatedAt: `${unified.actionAt}`,
  };
}

export function toSentenceCase(s: string) {
  return s.slice(0, 1).toUpperCase() + s.slice(1).toLowerCase();
}
